import React from 'react';
import SequentialVideoPlayer from './SequentialVideoPlayer';
import { isSequentialLessonUnlocked } from '../../utils/lessonSequence';

const youtubeIdFromUrl = (url = '') => {
  const value = String(url);
  return value.match(/youtube(?:-nocookie)?\.com\/embed\/([^?&/]+)/i)?.[1]
    || value.match(/[?&]v=([^?&/]+)/i)?.[1]
    || value.match(/youtu\.be\/([^?&/]+)/i)?.[1]
    || '';
};

const isDirectVideo = (resource, url) => (
  String(resource?.mimeType || '').startsWith('video/')
  || /\.(mp4|webm|ogg|mov)(?:$|[?#])/i.test(String(url || ''))
);

export default function EmbeddedVideoCard({
  resource,
  sourceUrl,
  lessons = [],
  completedLessonIds = new Set(),
  hasAccess = true,
  onComplete,
  onSelectLesson,
  onClose
}) {
  if (!resource || !sourceUrl) return null;
  const isSequential = Boolean(resource.isMillionairesConnectVideo);
  const complete = isSequential && completedLessonIds.has(resource.lessonId);
  const nextLesson = isSequential ? (lessons[resource.sequenceIndex + 1] || null) : null;
  const nextUnlocked = nextLesson && isSequentialLessonUnlocked({
    lessons,
    lessonIndex: nextLesson.sequenceIndex,
    completedLessonIds,
    hasAccess
  });
  const youtubeId = resource.videoId || youtubeIdFromUrl(sourceUrl);

  return (
    <section className="mc-inline-player-card" aria-labelledby={`player-${resource.id}`}>
      <header>
        <div>
          <span>{isSequential ? `Lesson ${resource.sequenceNumber} of ${resource.sequenceTotal}` : 'Course video'}</span>
          <h2 id={`player-${resource.id}`}>{resource.title}</h2>
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Close video</button>
      </header>
      <div className="mc-inline-player-body">
        {isSequential ? (
          <SequentialVideoPlayer
            videoId={youtubeId}
            title={`${resource.sequenceNumber}. ${resource.title}`}
            onEnded={() => onComplete?.(resource)}
          />
        ) : youtubeId ? (
          <div className="mc-video-player">
            <iframe
              title={resource.title}
              src={`https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0&playsinline=1`}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
        ) : isDirectVideo(resource, sourceUrl) ? (
          <div className="mc-video-player">
            <video
              src={sourceUrl}
              title={resource.title}
              controls
              controlsList="nodownload noremoteplayback"
              disablePictureInPicture
              playsInline
            />
          </div>
        ) : (
          <div className="mc-video-player">
            <iframe
              title={resource.title}
              src={sourceUrl}
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
        )}
        <div className="mc-player-guidance">
          <span>Embedded course video</span>
          <p>{isSequential ? 'Watch at least 90% here to complete this lesson and unlock the next one.' : 'This video plays directly inside your student portal.'}</p>
        </div>
        {complete && (
          <div className="mc-completion-notice" role="status">
            <div>
              <strong>Lesson {resource.sequenceNumber} completed</strong>
              <span>Your progress has been saved.</span>
            </div>
            {nextLesson && nextUnlocked && (
              <button type="button" className="btn btn-primary btn-sm" onClick={() => onSelectLesson?.(nextLesson)}>
                Play lesson {nextLesson.sequenceNumber}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
